<script lang="ts">
	// Formatters/i18n
	import { format } from 'svelte-i18n';

	// Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	// Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';

	// Design system components
	import { Alert, Toggle, toast } from '@Shelly/shelly-design-system';

	// Helpers
	import { get_local_ip, is_gen_2, real_id } from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';

	// Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { localReq, postReq } from 'src/services/http';

	// Constants
	import {
		DIMMERS_WITH_TWO_INPUTS,
		NO_SWITCH_RESET,
		PM_DEVICES_WITH_TWO_INPUTS_IN_COVER_MODE,
	} from 'src/constants';

	// Configs
	import { devices_config } from '@devices/index';

	export let device: Device;

	const id = real_id(device.id);
	const gen2 = is_gen_2(device);
	const localIp = get_local_ip(id);
	const isPmDimmerWithTwoInputs = DIMMERS_WITH_TWO_INPUTS.includes(device.type);

	let settings = devices_settings.getByRealId(id);

	$: isCoverProfile = !!settings?.['cover:0'];
	$: showBothInputsToggle =
		isPmDimmerWithTwoInputs ||
		(isCoverProfile && PM_DEVICES_WITH_TWO_INPUTS_IN_COVER_MODE.includes(device.type));
	$: hasResetFromInput =
		devices_config?.[device?.type]?.reset_from_input &&
		(gen2 ? typeof settings?.[`input:${device.channel}`]?.factory_reset !== 'undefined' : true);
	$: hasResetFromSwitch = NO_SWITCH_RESET.indexOf(device.type) === -1 && !gen2;

	let checked = !gen2
		? settings?.factory_reset_from_switch
		: settings?.[`input:${device.channel}`]?.factory_reset;
	let checked1 = settings?.['input:1']?.factory_reset;

	const getFinalResponse = (results: SettingRequest[]) => {
		const different = results.find((r) => !r.isok || r.data?.restart_required);
		if (different) {
			return different;
		}
		return results[0];
	};

	const handleToggler = (channel?: number) => {
		if (!channel) {
			checked = !checked;
		} else {
			checked1 = !checked1;
		}
		saveExternal(channel);
	};

	const saveExternal = async (channel?: number) => {
		const flag: boolean = !channel ? checked : checked1;
		const stringState = flag
			? $format('reset_from_switch_enabled_scc_toast')
			: $format('reset_from_switch_disabled_scc_toast');
		if (gen2) {
			if (!isPmDimmerWithTwoInputs && device.channel === 0 && device?.channels_count <= 1) {
				const inputKeys = Object.keys(settings).filter((key) => key.startsWith('input:'));
				const results: SettingRequest[] = [];
				for (const key of inputKeys) {
					const channelId = Number(key.split(':')[1]);
					const res = await gen2Request(device, 'input.setconfig', {
						id: channelId,
						config: { factory_reset: flag },
					});
					if (res?.isok) {
						settings[key].factory_reset = flag;
						devices_settings.update_devices_settings(id, settings);
					}
					results.push(res);
				}
				onSettingsResponse(getFinalResponse(results), device);
			} else {
				const res = await gen2Request(device, 'input.setconfig', {
					id: channel || device.channel,
					config: { factory_reset: flag },
				});
				const response = onSettingsResponse(res, device);
				if (response) {
					settings[`input:${channel || device.channel}`].factory_reset = flag;
					devices_settings.update_devices_settings(id, settings);
				}
			}
		} else {
			let res;

			if (localIp) {
				res = await localReq(device, `${localIp}/settings`, 'post', {
					factory_reset_from_switch: flag,
				});
			} else {
				res = await postReq(`/device/reset_from_switch`, {
					id: id,
					reset: flag ? 1 : 0,
				});
			}
			if (res.isok) {
				toast.open({ type: 'success', title: stringState });
			} else {
				if (!channel) {
					checked = !checked;
				} else {
					checked1 = !checked1;
				}
				toast.open({
					type: 'danger',
					title: $format('reset_from_switch_err_toast'),
				});
			}
		}
	};
</script>

{#if hasResetFromSwitch || hasResetFromInput}
	<Alert class="mb-s" type="info" heading={$format('alert_reset_device_from_input', {
		default: 'By enabling this setting you can reset the device from the device input.',
	})}/>
	<Toggle
		class="mb-s"
		size="m"
		{checked}
		on:click={() => handleToggler()}
		id="toggle-factory-reset-from-input"
	>
		{showBothInputsToggle
			? $format('label_factory_reset_enable_from_input_0', {
					default: 'Enable factory reset from input 0',
				})
			: $format('label_factory_reset_enable_from_input', {
					default: 'Enable factory reset from input',
				})}
	</Toggle>
	{#if showBothInputsToggle}
		<Toggle
			class="mb-s"
			size="m"
			checked={checked1}
			on:click={() => handleToggler(1)}
			id="toggle-factory-reset-from-input-1"
		>
			{$format('label_factory_reset_enable_from_input_1', {
				default: 'Enable factory reset from input 1',
			})}
		</Toggle>
	{/if}
{/if}
